import { useCallback, useEffect, useState, type ReactNode } from "react";
import { projectToast } from "@project/lib/toast";
import { getApiErrorMessage } from "@project/lib/api-error";
import { useI18n } from "@project/lib/use-i18n";
import {
  FormStepper,
  type FormStep,
} from "@project/user/components/form-stepper";
import { Input } from "@project/components/ui/input";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import { Button } from "@project/components/ui/button";
import { FamilyDetails } from "@project/admin/components/family-details";
import {
  createFamilyMemberRows,
  type FamilyMember,
} from "@project/admin/components/family-details-constants";
import { SearchableSelect } from "@project/admin/components/searchable-select";
import { useLocationCascade } from "@project/components/location-cascading-selects";
import { relationshipService } from "@project/services/admin/relationship.service";
import { elderAdmissionService } from "@project/services/user/elder-admission.service";
import type { ElderAdmissionRecord } from "@project/types/elder-admission";
import {
  admissionRecordToFamilyMembers,
  admissionRecordToFormState,
  buildAdmissionPayload,
  validateAdmissionForm,
  validateAdmissionStep,
  type AdmissionFormState,
} from "@project/components/admission-application-form.utils";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { DEFAULT_PHONE_COUNTRY } from "@project/lib/mobile-number";
import {
  User,
  Heart,
  Phone,
  Wallet,
  Send,
  Users2,
  ChevronLeft,
  ChevronRight,
  Save,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@project/lib/utils";

type StepKey = "applicant" | "elder" | "income" | "contacts" | "family";

type StepConfig = FormStep & {
  icon: LucideIcon;
  eyebrow: string;
  subtitle: string;
};

export type { AdmissionFormState } from "@project/components/admission-application-form.utils";

const INITIAL_FORM: AdmissionFormState = {
  applicantName: "",
  applicantMobile: "",
  applicantMobileCountry: DEFAULT_PHONE_COUNTRY,
  applicantAddress: "",
  relationshipWithElder: "",
  elderName: "",
  fatherName: "",
  age: "",
  currentLivingSituation: "",
  physicalCondition: "",
  reasonForAdmission: "",
  pensionDetails: "",
  otherIncome: "",
  emergencyContact1: "",
  emergencyContact2: "",
  emergencyContact3: "",
  additionalNotes: "",
};

const STEP_DEFS: { key: StepKey; icon: LucideIcon }[] = [
  { key: "applicant", icon: User },
  { key: "elder", icon: Heart },
  { key: "income", icon: Wallet },
  { key: "contacts", icon: Phone },
  { key: "family", icon: Users2 },
];

function StepPanel({
  step,
  children,
}: {
  step: StepConfig;
  children: ReactNode;
}) {
  const Icon = step.icon;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-border pb-4">
        <div className="bg-primary/10 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
            {step.eyebrow}
          </div>
          <h2 className="font-serif text-lg font-bold text-foreground sm:text-xl">
            {step.label}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
            {step.subtitle}
          </p>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  full = false,
  required = false,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
  required?: boolean;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-foreground/80 mb-1.5 block text-xs font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}

function patchFormField<K extends keyof AdmissionFormState>(
  key: K,
  value: AdmissionFormState[K]
) {
  return (prev: AdmissionFormState) => ({ ...prev, [key]: value });
}

export type AdmissionApplicationFormProps = {
  formId?: string;
  mode?: "user" | "admin";
  className?: string;
  editingId?: number;
  initialRecord?: ElderAdmissionRecord;
  onSubmitted?: (summary: { applicantName: string; elderName: string }) => void;
};

export function AdmissionApplicationForm({
  formId = "admission-application-form",
  mode = "user",
  className,
  editingId,
  initialRecord,
  onSubmitted,
}: AdmissionApplicationFormProps) {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<AdmissionFormState>(INITIAL_FORM);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>(() =>
    createFamilyMemberRows(3)
  );
  const location = useLocationCascade();
  const { setValue: setLocationValue } = location;
  const steps: StepConfig[] = STEP_DEFS.map(def => ({
    icon: def.icon,
    label: t(`account.admissionForm.steps.${def.key}.label`),
    shortLabel: t(`account.admissionForm.steps.${def.key}.shortLabel`),
    eyebrow: t(`account.admissionForm.steps.${def.key}.eyebrow`),
    subtitle: t(`account.admissionForm.steps.${def.key}.subtitle`),
  }));
  const current = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const isAdmin = mode === "admin";
  const isEditing = editingId !== undefined;

  useEffect(() => {
    if (!initialRecord) {
      return;
    }
    setForm(admissionRecordToFormState(initialRecord));
    setFamilyMembers(admissionRecordToFamilyMembers(initialRecord));
    setLocationValue({
      districtId: String(initialRecord.district),
      cityId: String(initialRecord.city),
      areaId: String(initialRecord.area),
    });
    setStep(0);
  }, [initialRecord, editingId, setLocationValue]);

  const setField = <K extends keyof AdmissionFormState>(
    key: K,
    value: AdmissionFormState[K]
  ) => {
    setForm(patchFormField(key, value));
  };

  const searchRelationships = useCallback(async (query: string) => {
    const result = await relationshipService.listRelationshipsPage({
      page: 1,
      page_size: 50,
      search: query || undefined,
    });
    return result.data
      .filter(r => r.is_active)
      .map(r => ({ value: String(r.id), label: r.relationship_name }));
  }, []);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setFamilyMembers(createFamilyMemberRows(3));
    location.setValue({ districtId: "", cityId: "", areaId: "" });
    setStep(0);
  };

  const goNext = () => {
    if (isLast) {
      return;
    }
    const stepError = validateAdmissionStep(step, form, location.value);
    if (stepError) {
      projectToast.error(t(stepError));
      return;
    }
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (isFirst) {
      return;
    }
    setStep(s => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    const validationError = validateAdmissionForm(form, location.value);
    if (validationError) {
      projectToast.error(t(validationError.messageKey));
      setStep(validationError.step);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildAdmissionPayload(
        form,
        location.value,
        familyMembers
      );
      const summary = {
        applicantName: form.applicantName.trim(),
        elderName: form.elderName.trim(),
      };
      if (isEditing) {
        await elderAdmissionService.update(editingId, payload);
        if (isAdmin) {
          projectToast.success(t("account.admissionForm.toast.updated"));
        }
      } else {
        await elderAdmissionService.create(payload);
        if (isAdmin) {
          projectToast.success(t("account.admissionForm.toast.saved"));
          resetForm();
        }
      }
      onSubmitted?.(summary);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(
          error,
          isEditing
            ? t("account.admissionForm.toast.updateFailed")
            : t("account.admissionForm.toast.submitFailed")
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    const target = e.target as HTMLElement;
    if (target.tagName === "TEXTAREA") {
      return;
    }
    e.preventDefault();
    if (isLast) {
      void handleSubmit();
    } else {
      goNext();
    }
  };

  return (
    <div className={cn(className)}>
      <FormStepper steps={steps} currentStep={step} className="mb-8" />

      <form
        id={formId}
        onSubmit={e => {
          e.preventDefault();
          void handleSubmit();
        }}
        onKeyDown={handleFormKeyDown}
        className="space-y-6"
      >
        {step === 0 && (
          <StepPanel step={current}>
            <Field
              label={t("account.admissionForm.fields.applicantName.label")}
              required
            >
              <Input
                value={form.applicantName}
                onChange={e => setField("applicantName", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.applicantName.placeholder"
                )}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.mobile.label")}
              required
            >
              <MobileNumberField
                value={form.applicantMobile}
                country={form.applicantMobileCountry}
                onCountryChange={code =>
                  setField("applicantMobileCountry", code)
                }
                onChange={value => setField("applicantMobile", value)}
                placeholder={t(
                  "account.admissionForm.fields.mobile.placeholder"
                )}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.address.label")}
              full
              required
            >
              <Textarea
                rows={2}
                value={form.applicantAddress}
                onChange={e => setField("applicantAddress", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.address.placeholder"
                )}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.relationship.label")}
              required
            >
              <SearchableSelect
                value={form.relationshipWithElder}
                onValueChange={v => setField("relationshipWithElder", v)}
                onSearch={searchRelationships}
                placeholder={t(
                  "account.admissionForm.fields.relationship.placeholder"
                )}
                searchPlaceholder={t(
                  "account.admissionForm.fields.relationship.searchPlaceholder"
                )}
                emptyText={t(
                  "account.admissionForm.fields.relationship.emptyText"
                )}
              />
            </Field>
          </StepPanel>
        )}

        {step === 1 && (
          <StepPanel step={current}>
            <Field
              label={t("account.admissionForm.fields.elderName.label")}
              required
            >
              <Input
                value={form.elderName}
                onChange={e => setField("elderName", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.elderName.placeholder"
                )}
              />
            </Field>
            <Field label={t("account.admissionForm.fields.fatherName.label")}>
              <Input
                value={form.fatherName}
                onChange={e => setField("fatherName", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.fatherName.placeholder"
                )}
              />
            </Field>
            <Field label={t("account.admissionForm.fields.age.label")} required>
              <Input
                type="number"
                value={form.age}
                onChange={e => setField("age", e.target.value)}
                placeholder={t("account.admissionForm.fields.age.placeholder")}
                min={0}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.district.label")}
              required
            >
              <SearchableSelect
                value={location.value.districtId}
                onValueChange={location.setDistrictId}
                onSearch={location.searchDistricts}
                placeholder={t(
                  "account.admissionForm.fields.district.placeholder"
                )}
                searchPlaceholder={t(
                  "account.admissionForm.fields.district.searchPlaceholder"
                )}
                emptyText={t("account.admissionForm.fields.district.emptyText")}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.city.label")}
              required
            >
              <SearchableSelect
                value={location.value.cityId}
                onValueChange={location.setCityId}
                onSearch={location.searchCities}
                placeholder={location.cityPlaceholder}
                searchPlaceholder={t(
                  "account.admissionForm.fields.city.searchPlaceholder"
                )}
                emptyText={
                  location.cityDisabled
                    ? t("account.admissionForm.fields.city.emptyDisabled")
                    : t("account.admissionForm.fields.city.empty")
                }
                disabled={location.cityDisabled}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.area.label")}
              required
            >
              <SearchableSelect
                value={location.value.areaId}
                onValueChange={location.setAreaId}
                onSearch={location.searchAreas}
                placeholder={location.areaPlaceholder}
                searchPlaceholder={t(
                  "account.admissionForm.fields.area.searchPlaceholder"
                )}
                emptyText={
                  location.areaDisabled
                    ? t("account.admissionForm.fields.area.emptyDisabled")
                    : t("account.admissionForm.fields.area.empty")
                }
                disabled={location.areaDisabled}
              />
            </Field>
            <Field
              label={t(
                "account.admissionForm.fields.currentLivingSituation.label"
              )}
              full
            >
              <Textarea
                rows={2}
                value={form.currentLivingSituation}
                onChange={e =>
                  setField("currentLivingSituation", e.target.value)
                }
                placeholder={t(
                  "account.admissionForm.fields.currentLivingSituation.placeholder"
                )}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.physicalCondition.label")}
              full
            >
              <Textarea
                rows={2}
                value={form.physicalCondition}
                onChange={e => setField("physicalCondition", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.physicalCondition.placeholder"
                )}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.reasonForAdmission.label")}
              full
              required
            >
              <Textarea
                rows={3}
                value={form.reasonForAdmission}
                onChange={e => setField("reasonForAdmission", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.reasonForAdmission.placeholder"
                )}
              />
            </Field>
          </StepPanel>
        )}

        {step === 2 && (
          <StepPanel step={current}>
            <Field
              label={t("account.admissionForm.fields.pensionDetails.label")}
            >
              <Input
                value={form.pensionDetails}
                onChange={e => setField("pensionDetails", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.pensionDetails.placeholder"
                )}
              />
            </Field>
            <Field label={t("account.admissionForm.fields.otherIncome.label")}>
              <Input
                value={form.otherIncome}
                onChange={e => setField("otherIncome", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.otherIncome.placeholder"
                )}
              />
            </Field>
          </StepPanel>
        )}

        {step === 3 && (
          <StepPanel step={current}>
            <Field
              label={t("account.admissionForm.fields.contact1.label")}
              required
            >
              <Input
                value={form.emergencyContact1}
                onChange={e => setField("emergencyContact1", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.contact1.placeholder"
                )}
              />
            </Field>
            <Field label={t("account.admissionForm.fields.contact2.label")}>
              <Input
                value={form.emergencyContact2}
                onChange={e => setField("emergencyContact2", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.contact2.placeholder"
                )}
              />
            </Field>
            <Field
              label={t("account.admissionForm.fields.contact3.label")}
              full
            >
              <Input
                value={form.emergencyContact3}
                onChange={e => setField("emergencyContact3", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.contact3.placeholder"
                )}
              />
            </Field>
          </StepPanel>
        )}

        {step === 4 && (
          <StepPanel step={current}>
            <div className="md:col-span-2">
              <FamilyDetails
                embedded
                value={familyMembers}
                onChange={setFamilyMembers}
                onSearchRelationship={searchRelationships}
              />
            </div>
            <Field
              label={t("account.admissionForm.fields.additionalRemarks.label")}
              full
            >
              <Textarea
                rows={4}
                value={form.additionalNotes}
                onChange={e => setField("additionalNotes", e.target.value)}
                placeholder={t(
                  "account.admissionForm.fields.additionalRemarks.placeholder"
                )}
              />
            </Field>
            {!isAdmin && (
              <div className="border-primary/30 bg-primary/5 rounded-xl border border-dashed p-4 md:col-span-2">
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t("account.admissionForm.consent")}
                </p>
              </div>
            )}
          </StepPanel>
        )}

        <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
          <Button
            type="button"
            variant="outline"
            onClick={goBack}
            disabled={isFirst}
            className="gap-1 rounded-xl"
          >
            <ChevronLeft className="h-4 w-4" />{" "}
            {t("account.admissionForm.nav.previous")}
          </Button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {!isLast && (
              <p className="text-center text-xs text-muted-foreground sm:text-right">
                {steps.length - step - 1 === 1
                  ? t("account.admissionForm.nav.remainingOne", {
                      count: steps.length - step - 1,
                    })
                  : t("account.admissionForm.nav.remainingOther", {
                      count: steps.length - step - 1,
                    })}
              </p>
            )}
            {isLast ? (
              <Button
                type="submit"
                disabled={submitting}
                className={cn(
                  "gap-1 rounded-xl",
                  isAdmin ? "" : "hover:bg-gold/90 bg-gold text-gold-foreground"
                )}
              >
                {isAdmin ? (
                  <Save className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {submitting
                  ? t("account.admissionForm.submit.saving")
                  : isAdmin
                    ? isEditing
                      ? t("account.admissionForm.submit.update")
                      : t("account.admissionForm.submit.save")
                    : t("account.admissionForm.submit.submit")}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={e => {
                  e.preventDefault();
                  goNext();
                }}
                className="gap-1 rounded-xl"
              >
                {t("account.admissionForm.nav.next")}{" "}
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
